import React, { useEffect, useState } from 'react'
import './index.css'
import '../Filters/index.css'
import ResolutionTimeTableWidget from '../reportWidgets/ResolutionTimeTable';
import ResolutionTimeChartWidget from '../reportWidgets/ResolutionTimeChart';
import { AggDateRangeCountModel, ClosedTicketReportFilters } from '../../../models/reports';
import { FilterChipOutlined } from '../../common/ActionChip/FilterChip';
import { CalendarRange, DateRange, DaysRange, getLastNDaysRange, getLastNMonthsRange, MonthRange } from '../../BasicCalendar/Utils';
import { useProject } from '../../../context/ProjectContext';
import { aggDateRangeCountFilter } from '../../../services/report.service';
import { APIResponseModel } from '../../../models/api-call';
import BasicCalendarRange from '../../BasicCalendar/BasicCalendarRange';
import dayjs from 'dayjs';
import ClosedTicketAmountChartWidget from '../reportWidgets/ClosedTicketAmountChart';
import IndicatorChip from '../../common/ActionChip/IndicatorChip';
import Autocomplete from '../../common/Autocomplete';
import { itemSelection } from '../../../models/itemSelection.model';
import { getAreasByCompany, getCompanies, getTicketPriorities } from '../../../services/autocomplete.service';
import { useNotification } from '../../../context/NotificationContext';


interface ClosedTicketsReportProps {
  setBlockScreen: (p: boolean) => void
}

const ClosedTicketsReport: React.FC<ClosedTicketsReportProps> = ({ setBlockScreen }) => {

  const { selectedProject } = useProject()
  const { showNotification } = useNotification();

  type filterDateType = `days${number}` | `months${number}` | 'calendar' | ''

  const [widthAvailable, setWidthAvailable] = useState<'halfw' | 'fullw'>('halfw')

  const [currentFilterDate, setCurrentFilterDate] = useState<filterDateType>('')
  const [aggDateRangeCount, setAggDateRangeCount] = useState<AggDateRangeCountModel>()

  const last7days: DaysRange = getLastNDaysRange(7)
  const last14days: DaysRange = getLastNDaysRange(14)
  const last3months: MonthRange[] = getLastNMonthsRange(3)

  const [companiesList, setCompaniesList] = useState<itemSelection[]>([]);
  const [areasList, setAreasList] = useState<itemSelection[]>([]);
  const [prioritiesList, setPrioritiesList] = useState<itemSelection[]>([]);

  const [reloadWidgets, setReloadWidgets] = useState<boolean>(false)

  const defaultFilter: ClosedTicketReportFilters = {
    from: last7days.range.from,
    to: last7days.range.to,
    company: { id: '', label: '' },
    areas: [],
    priority: { id: '', label: '' },
  }

  const [reportFilters, setReportFilters] = useState<ClosedTicketReportFilters>(defaultFilter)

  const initComponent = () => {
    setBlockScreen(true)
    Promise.all([
      getCompanies(selectedProject),
      getTicketPriorities(selectedProject),
    ]).then(function (result) {
      setCompaniesList(result[0])
      setPrioritiesList(result[1])
      setBlockScreen(false)
    }).catch((error) => {
      setBlockScreen(false)
      showNotification(error.response.data)
    });
    setReportFilters(defaultFilter)
    setCurrentFilterDate(last7days.id)
  }

  useEffect(initComponent, [selectedProject])

  const reloadAggDateRangeCount = () => {
    Promise.all([aggDateRangeCountFilter(selectedProject, { area: defaultFilter.areas })])
      .then((result: APIResponseModel[] | any) => {
        setAggDateRangeCount(result[0].data)
      })
      .catch((error) => {
        console.log(error.response.data.message)
      })
  }

  useEffect(reloadAggDateRangeCount, [reportFilters.areas, selectedProject])

  useEffect(() => {
    const dateFrom = dayjs(reportFilters.from)
    const dateTo = dayjs(reportFilters.to)
    const days = dateTo.diff(dateFrom, 'day')
    setWidthAvailable(days < 15 ? 'halfw' : 'fullw')
  }, [reportFilters.from, reportFilters.to])

  const onQuickFilterDate = (filterDate: DaysRange | MonthRange | CalendarRange) => {
    setCurrentFilterDate(filterDate.id)
    setReportFilters({ ...reportFilters, from: filterDate.range.from, to: filterDate.range.to })
  }

  // temporal handleSelection until areas accept multiple values to append
  const handleSelection = (key: string, value: any): void => {
    let newValue: itemSelection | itemSelection[]
    if (key === 'areas') {
      newValue = value === '' ? [] : [value]
    } else {
      newValue = value === '' ? { id: '', label: '' } : value
    }
    setReportFilters({
      ...reportFilters,
      [key]: newValue
    });
  }

  const getAreasCompany = () => {
    handleSelection('areas', '')
    setAreasList([])
    if (reportFilters.company.id) {
      Promise.all([
        getAreasByCompany(reportFilters.company.id as string, selectedProject),
      ]).then(function (result) {
        setAreasList(result[0])
      }).catch((error) => {
        showNotification(error.response.data, 'error')
      });
    }
  }

  useEffect(getAreasCompany, [reportFilters.company.id])

  useEffect(() => { setReloadWidgets(!reloadWidgets) },
    [reportFilters.from,
    reportFilters.to,
    reportFilters.company.id,
    reportFilters.priority.id,
    // reportFilters.areas[0].id,
    reportFilters.areas[0],
      selectedProject
    ])

  return (
    <React.Fragment>
      <div className='closed-ticket-report'>
        <div className='closed-ticket-report-date-filters'>
          <span className='report-quickfilter-chip'>
            <FilterChipOutlined
              label={last7days.label}
              onClick={() => onQuickFilterDate(last7days)}
              active={currentFilterDate === 'days7'}
              counter={aggDateRangeCount?.last7days} />
          </span>

          <span className='report-quickfilter-chip'>
            <FilterChipOutlined
              label={last14days.label}
              onClick={() => onQuickFilterDate(last14days)}
              active={currentFilterDate === 'days14'}
              counter={aggDateRangeCount?.last14days} />
          </span>

          <span className='report-quickfilter-chip'>

            <FilterChipOutlined
              label={last3months[0].label}
              onClick={() => onQuickFilterDate(last3months[0])}
              active={currentFilterDate === last3months[0].id}
              counter={aggDateRangeCount?.months1} />
          </span>

          <span className='report-quickfilter-chip'>
            <FilterChipOutlined
              label={last3months[1].label}
              onClick={() => onQuickFilterDate(last3months[1])}
              active={currentFilterDate === last3months[1].id}
              counter={aggDateRangeCount?.months2} />
          </span>

          <span className='report-quickfilter-chip'>

            <FilterChipOutlined
              label={last3months[2].label}
              onClick={() => onQuickFilterDate(last3months[2])}
              active={currentFilterDate === last3months[2].id}
              counter={aggDateRangeCount?.months3} />
          </span>

          <span className='report-quickfilter-chip'>
            <BasicCalendarRange
              active={currentFilterDate === 'calendar'}
              textControlButton='Personalizado'
              initialFrom={new Date(reportFilters.from)}
              initialTo={new Date(reportFilters.to)}
              onConfirm={(f: DateRange) => { onQuickFilterDate({ id: 'calendar', range: { from: f.from, to: f.to } }) }
              }
            />
          </span>

          <span className='report-quickfilter-chip datefilter-info'>
            <IndicatorChip
              label={<span>
                <span className="applied-filters__name">Fecha:</span> {reportFilters.from} — {reportFilters.to}
              </span>}
            />

          </span>

        </div>

        <div className='closed-ticket-report-secondary-filters'>
          <span className='report-filter-selection'>
            <Autocomplete
              label='Compañía'
              options={companiesList}
              handleSelection={handleSelection}
              value={reportFilters.company}
              name='company'
            />
          </span>

          <span className='report-filter-selection'>
            <Autocomplete
              label='Área'
              options={areasList}
              handleSelection={handleSelection}
              value={reportFilters.areas[0]}
              name='areas'
            />
          </span>

          <span className='report-filter-selection'>
            <Autocomplete
              label='Prioridad'
              options={prioritiesList}
              handleSelection={handleSelection}
              value={reportFilters.priority}
              name='priority'
            />
          </span>

          {reportFilters.company.label && (
            <span className='selectionfilter-info'>
              <IndicatorChip
                label={<span>
                  <span className="applied-filters__name">Compañía:</span> {reportFilters.company.label}
                </span>}
              />
            </span>
          )}

          {reportFilters.areas[0]?.label && (
            <span className='selectionfilter-info'>
              <IndicatorChip
                label={<span>
                  <span className="applied-filters__name">Área:</span> {reportFilters.areas[0].label}
                </span>}
              />
            </span>
          )}

          {reportFilters.priority.label && (
            <span className='selectionfilter-info'>
              <IndicatorChip
                label={<span>
                  <span className="applied-filters__name">Prioridad:</span> {reportFilters.priority.label}
                </span>}
              />
            </span>
          )}

          {/* temporary deactivated btns for applying filters - maybe unnecesary btns? */}
          <div style={{ display: 'none' }} className='report-secondary-filters-actions'>
            <div className="report-secondary-filters-btns">
              <button
                className='report-secondary-filters-btns-confirm'
                onClick={() => { }}
              >
                Aplicar
              </button>

              <button className='report-secondary-filters-btns-cancel'
                onClick={() => { }}>Restablecer
              </button>
            </div>
          </div>

        </div>

        <div className='closed-ticket-report-charts'>

          <div className={`closed-tickets-chart ${widthAvailable}`}>
            <ClosedTicketAmountChartWidget
              title='Tickets Cerrados por Día'
              filters={reportFilters}
              reload={reloadWidgets}
            />
          </div>

          <div className={`closed-tickets-chart ${widthAvailable}`}>
            <ResolutionTimeChartWidget
              title='Tiempo de Resolución Promedio'
              filters={reportFilters}
              reload={reloadWidgets}
            />
          </div>

        </div>
        <div>
          <ResolutionTimeTableWidget
            filters={reportFilters}
            reload={reloadWidgets}
          />
        </div>

      </div>
    </React.Fragment>
  )
}

export default ClosedTicketsReport;

